import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, EyeOff, MapPinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Disclosure } from "@/components/ui/disclosure";
import { CountPill } from "../facilities/CountPill.js";
import type { RoleGroup } from "./role-groups.js";

/**
 * The roles tree (ADR-030 §3.4): one collapsible section per prefix group, the FIRST expanded (legacy), one button per
 * role showing its short name. The selected role carries `aria-current="true"`. A hidden role (supers only) shows an
 * "Hidden" badge; a role with no level set shows the legacy's warning glyph so a super finds unconfigured roles fast, and
 * a role with a level but NO facility grant shows the legacy dashboard's "no facilities assigned" hint as a marker
 * (Phase D) — together they are the admin's to-do list.
 *
 * The groups are an ACCORDION (owner): opening one closes the rest, so a long realm never grows a scrolling column;
 * the selected role's group is the one open (a deep link or a click elsewhere moves it), the first when nothing is
 * selected. `query` filters roles by name (case-insensitive, the FULL name so the group prefix matches too): while a
 * filter is active every group with a match is open and the accordion rests.
 */
export function RoleTree({ groups, selectedId, onSelect, query = "" }: { groups: RoleGroup[]; selectedId: number | null; onSelect: (id: number) => void; query?: string }) {
  const { t } = useTranslation();
  const keyOf = (g: RoleGroup) => g.key ?? "__none";
  const selectedKey = useMemo(() => {
    const g = selectedId === null ? undefined : groups.find((x) => x.roles.some((r) => r.id === selectedId));
    return g ? keyOf(g) : null;
  }, [groups, selectedId]);
  const [openKey, setOpenKey] = useState<string | null>(() => selectedKey ?? (groups[0] ? keyOf(groups[0]) : null));
  // A selection landing in ANOTHER group (deep link, a stale-dropped id) opens that group.
  useEffect(() => {
    if (selectedKey !== null) setOpenKey(selectedKey);
  }, [selectedKey]);
  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (q ? groups.map((g) => ({ ...g, roles: g.roles.filter((r) => r.name.toLowerCase().includes(q)) })).filter((g) => g.roles.length > 0) : groups),
    [groups, q],
  );
  if (shown.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("roles.noRoleMatches", { query: query.trim() })}</p>;
  }
  return (
    <ul aria-label={t("roles.roleGroups")} className="m-0 flex list-none flex-col gap-1 p-0">
      {shown.map((g) => (
        <li key={keyOf(g)}>
          <Disclosure
            isExpanded={q ? true : openKey === keyOf(g)}
            onExpandedChange={(open) => setOpenKey(open ? keyOf(g) : null)}
            title={
              <span className="flex items-center gap-1.5">
                <span className="truncate">{g.label ?? t("roles.noGroup")}</span>
                <CountPill n={g.roles.length} />
              </span>
            }
          >
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0 pl-3">
              {g.roles.map((r) => {
                const current = r.id === selectedId;
                return (
                  <li key={r.id}>
                    <Button
                      variant={current ? "secondary" : "ghost"}
                      size="sm"
                      aria-current={current ? "true" : undefined}
                      onPress={() => onSelect(r.id)}
                      className="w-full justify-start gap-2 border-0"
                    >
                      <span className="truncate">{r.shortName}</span>
                      {r.hpfp === null ? (
                        <AlertTriangle role="img" aria-label={t("roles.noPermissionLevelSet")} className="text-attention size-3.5 shrink-0" />
                      ) : r.grantCount === 0 ? (
                        <MapPinOff role="img" aria-label={t("roles.noFacilitiesAssigned")} className="text-attention size-3.5 shrink-0" />
                      ) : null}
                      {r.hidden ? (
                        <span className="bg-muted text-muted-foreground ml-auto inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase">
                          <EyeOff aria-hidden="true" className="size-3" />
                          {t("roles.hidden")}
                        </span>
                      ) : null}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </Disclosure>
        </li>
      ))}
    </ul>
  );
}
